/**
 * journey-model — Journeys editor CONFIG + pure helpers, ported from the source
 * prototype's journey-data.js (the config half only). Templates, text-key
 * catalog, tile presets, default journey shapes, deepMerge, brand-scan mock and
 * the render helpers (resolveTheme / journeyScreens / journeyServices / tileImage
 * / eventImage / journeyURL) are ui-config + pure logic and live here.
 *
 * SEED JOURNEY CONTENT does NOT live here (no CD-authored fixture data in
 * app/src): the bank journey records — with their embedded locations / staff /
 * services / events — are served by the declared listJourneys op from the
 * preview harness (specs/journeys-seed-package-2026-07-23.md), tagged
 * __source "cd-preview-seed".
 */

// Journey records are dynamic config objects served by the journeys op; a broad
// alias keeps the verbatim renderer/panels transcription faithful without
// fighting the type system. Everything AROUND them is typed: as of 2026-07-25
// the Journeys files carry declared shapes for props, booking state, themes,
// templates and the text catalog, and the whole tree passes the strict gate
// with 0 errors (changelog/2026-07-25T2100-journeys-type-debt-cleared.md).
// Hardening these two aliases to precise record shapes is the remaining step
// and wants the real contract shape from the journeys op (REQ-063).
export type Journey = any; // eslint-disable-line @typescript-eslint/no-explicit-any
export type Patch = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/* Public booking domain — tenant-scoped at RUNTIME: JourneysHome sets it from
 * the tenant-brand op response (`TenantBrand.domain`); until then a neutral
 * placeholder. No real tenant's domain ships as a compile-time literal
 * (tenant-agnosticism, CC GM14 §4 items 1/3). ESM live binding — consumers
 * importing `baseURL` see updates. */
export let baseURL = "book.yourbrand.example";
export function setJourneysBaseURL(domain?: string | null) { if (domain && domain.trim()) baseURL = domain.trim(); }

/* Neutral brand fallback — ACTIVE_BRAND resolves from here ONLY until the
 * tenant-brand op responds (setActiveBrand). Deliberately tenant-less greys:
 * no tenant's palette is baked as source (CC GM14 §4 item 3). ui-config. */
export const BRAND_FALLBACK = {
  brandColor: "#2E3440", accentColor: "#8A94A6", backgroundColor: "#FAFAF9",
  textColor: "#14161E", headingFont: "DM Sans", bodyFont: "DM Sans", buttonShape: "rounded",
};
let ACTIVE_BRAND: Record<string, string> = { ...BRAND_FALLBACK };
export function setActiveBrand(b?: Record<string, string> | null) { if (b) ACTIVE_BRAND = { ...BRAND_FALLBACK, ...b }; }
export function getActiveBrand() { return ACTIVE_BRAND; }

import { getTenantVocab, type LobDef } from "../../lib/tenant-vocab";
/* Docket 2026-07-26 §1.2.1 (supersedes the packet §3.3 disposition): the
 * service-line vocabulary is SERVED tenant taxonomy — getTenantVocabulary
 * (REQ-091) through the typed client, @v0y4ge/api-mocks behind it — never a
 * constant in app source (the prior industry-keyed LOB_BY_INDUSTRY still
 * baked one vocabulary per vertical). Empty until the catalog resolves;
 * consumers fall back to the raw lob id. */
type Lob = LobDef;
export function linesOfBusiness(): Lob[] {
  return getTenantVocab()?.linesOfBusiness ?? [];
}

/* Text-catalog entry. Some keys are journey-type specific (`types`), most are
 * universal — declared here so the strict gate can check the access. */
export interface TextKey { key: string; group: string; label: string; def: string; types?: string[] }
/* ── Text catalog ──────────────────────────────────────────────────── */
export const TEXT_KEYS: TextKey[] = [
  { key: "welcome.headline", group: "Welcome", label: "Hero headline", def: "Book an appointment." },
  { key: "welcome.body", group: "Welcome", label: "Hero body", def: "Pick what you need, choose a place and time, and you're set. Most appointments confirm instantly." },
  { key: "service.headline", group: "Services", label: "Headline", def: "What can we help you with?" },
  { key: "service.subtitle", group: "Services", label: "Subtitle", def: "Choose a service — you can add more than one to the same visit." },
  { key: "location.headline", group: "Locations", label: "Headline", def: "Where works for you?" },
  { key: "location.subtitle", group: "Locations", label: "Subtitle", def: "Pick the location that suits you." },
  { key: "staff.headline", group: "Staff", label: "Headline", def: "Who would you like to meet with?" },
  { key: "staff.subtitle", group: "Staff", label: "Subtitle", def: "Pick a specialist, or take the first available." },
  { key: "staff.any", group: "Staff", label: "'Any staff' card", def: "First available" },
  { key: "time.headline", group: "Time", label: "Headline", def: "Pick a time" },
  { key: "time.subtitle", group: "Time", label: "Subtitle", def: "Times are shown in your local timezone." },
  { key: "details.headline", group: "Your details", label: "Headline", def: "Nearly there — your details" },
  { key: "details.subtitle", group: "Your details", label: "Subtitle", def: "We'll send your confirmation and reminders here." },
  { key: "consent.label", group: "Your details", label: "Consent statement", def: "I agree to the processing of my personal data as described in the Privacy Policy." },
  { key: "confirm.headline", group: "Confirmation", label: "Headline", def: "You're booked!" },
  { key: "confirm.subtitle", group: "Confirmation", label: "Subtitle", def: "A confirmation email is on its way with everything below." },
  { key: "btn.continue", group: "Buttons", label: "Continue button", def: "Continue" },
  { key: "btn.back", group: "Buttons", label: "Back link", def: "Back" },
  { key: "btn.confirm", group: "Buttons", label: "Confirm button", def: "Confirm booking", types: ["appointment", "hub"] },
  { key: "btn.reserve", group: "Buttons", label: "Reserve button", def: "Reserve my place", types: ["event"] },
  { key: "btn.waitlist", group: "Buttons", label: "Waitlist button", def: "Join the waitlist", types: ["event"] },
  { key: "btn.join", group: "Buttons", label: "Join queue button", def: "Join the queue", types: ["queue"] },
  { key: "ticket.headline", group: "Queue ticket", label: "Headline", def: "You're in line!", types: ["queue"] },
  { key: "ticket.subtitle", group: "Queue ticket", label: "Subtitle", def: "Keep this ticket handy — we'll text you as your turn gets close.", types: ["queue"] },
  { key: "ticket.up", group: "Queue ticket", label: "'You're up' message", def: "You're up — head to the counter and show this ticket.", types: ["queue"] },
  { key: "hub.events", group: "Hub", label: "Events module title", def: "What's on", types: ["hub"] },
  { key: "hub.back", group: "Hub", label: "Back-to-hub button", def: "Back to what's on", types: ["hub"] },
  { key: "hub.appointments", group: "Hub", label: "Appointments header", def: "Private appointments", types: ["hub"] },
  { key: "hub.queue", group: "Hub", label: "Queue header", def: "Join the line", types: ["hub"] },
  { key: "events.headline", group: "Events", label: "Headline", def: "What's on", types: ["event"] },
  { key: "events.subtitle", group: "Events", label: "Subtitle", def: "Browse what's coming up — reserve a place in a few taps.", types: ["event"] },
  { key: "evdone.headline", group: "Event confirmation", label: "Headline", def: "You're on the list!", types: ["event"] },
  { key: "evdone.subtitle", group: "Event confirmation", label: "Subtitle", def: "Your place is reserved — confirmation is on its way to your email.", types: ["event"] },
] ;
export const TEXT_DEFAULTS: Record<string, string> = Object.fromEntries(TEXT_KEYS.map((t) => [t.key, t.def]));
export function textOf(journey: Journey, key: string): string { return (journey.text && journey.text[key]) || TEXT_DEFAULTS[key] || key; }

/* ── Tile image presets ────────────────────────────────────────────── */
export const TILE_PRESETS = [
  { id: "brand", label: "Brand gradient", css: null as string | null },
  { id: "linen", label: "Linen", css: "linear-gradient(135deg, #EDE6D8, #D9CDB8)" },
  { id: "sage", label: "Sage", css: "linear-gradient(135deg, #5F7A5A, #A9BFA0)" },
  { id: "dusk", label: "Dusk", css: "linear-gradient(135deg, #2C3A54, #7A8BB0)" },
  { id: "clay", label: "Clay", css: "linear-gradient(135deg, #B0653C, #E0A57E)" },
  { id: "sand", label: "Sand", css: "radial-gradient(circle at 30% 20%, #F0E3C8, #C9A96A)" },
  { id: "slate", label: "Slate", css: "linear-gradient(135deg, #3F4250, #8A8FA3)" },
  { id: "atelier", label: "Atelier", css: "radial-gradient(circle at 28% 22%, rgba(255,246,230,0.5), transparent 55%), repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 3px), linear-gradient(135deg, #C0A377, #8A6D48 60%, #6B5236)" },
  { id: "noir", label: "Noir", css: "radial-gradient(circle at 72% 18%, rgba(198,164,118,0.55), transparent 55%), linear-gradient(160deg, #3E362B, #26221C 55%, #16130E)" },
  { id: "camel", label: "Camel", css: "repeating-linear-gradient(45deg, rgba(255,255,255,0.07) 0 2px, transparent 2px 7px), linear-gradient(135deg, #BE9D6C, #97744A)" },
  { id: "ivory", label: "Ivory", css: "radial-gradient(circle at 68% 26%, #FFFEFA, transparent 60%), linear-gradient(150deg, #F7F0E2, #E4D5B8 70%, #D3BE96)" },
];

/* ── deepMerge ─────────────────────────────────────────────────────── */
export function deepMerge(base: Journey, patch: Patch): Journey {
  if (!patch) return base;
  const out: Journey = Array.isArray(base) ? base.slice() : { ...base };
  for (const k of Object.keys(patch)) {
    out[k] = patch[k] && typeof patch[k] === "object" && !Array.isArray(patch[k]) && base[k] && typeof base[k] === "object"
      ? deepMerge(base[k], patch[k]) : patch[k];
  }
  return out;
}

/* ── Default journey shapes ────────────────────────────────────────── */
function baseJourney(): Journey {
  return {
    id: "jrn_" + Date.now().toString(36), name: "Untitled journey", slug: "new-journey", type: "appointment",
    status: "draft", createdBy: "", updatedAt: "Just now", publishedAt: null,
    views30d: 0, starts30d: 0, bookings30d: 0, templateId: "classic",
    theme: { inheritBrand: true, brandColor: "#5142C7", accentColor: "#EE9D4F", backgroundColor: "#FAFAF7", textColor: "#14161E", headingFont: "DM Sans", bodyFont: "DM Sans", buttonShape: "rounded", progress: "bar" },
    // Tenant-agnostic defaults (CC GM14 §4 item 4): no persona, phone number or
    // legal disclosure baked — the tenant fills these (or the server seed does).
    header: { style: "standard", showLogo: true, tagline: "", showPhone: false, phone: "" },
    footer: { show: true, showContact: true, contactCopy: "Questions? We're happy to help.", showLegal: true, legalCopy: "", showPolicyLinks: true },
    flowOrder: "service-first", allowBack: true, autoAdvance: false,
    steps: {
      service: { display: "list", showDuration: true, showDescription: true, groupBy: "line-of-business", allowMultiple: false, maxServices: 2, lobs: ["personal", "homeloans", "wealth", "business"], showImages: true, images: {} },
      location: { display: "map-list", radiusEnabled: true, radiusMi: 10, showHours: true, showServices: true, showDistance: true, showVideoOption: true, showPhoneOption: false },
      staff: { enabled: true, choice: "optional", showRole: true, showRating: false, showNextAvail: true, showBio: false },
      time: { window: "next-14-days", showOnlyAvailable: true, waitlist: true, multiStrategy: "prefer-same", bufferMin: 10 },
      details: {
        fields: [
          { id: "first", label: "First name", type: "text", required: true, locked: true },
          { id: "last", label: "Last name", type: "text", required: true, locked: true },
          { id: "email", label: "Email address", type: "email", required: true, locked: true },
          { id: "phone", label: "Phone number", type: "tel", required: true, locked: false },
          { id: "notes", label: "Anything you'd like us to prepare?", type: "longtext", required: false, locked: false },
        ],
        attendees: { enabled: true, max: 3, collectNames: true },
        consent: { required: true, policyUrl: "", marketingOptIn: true, marketingLabel: "Send me occasional news and updates.", whatsappOptIn: true, whatsappLabel: "I'd like to get updates about this booking on WhatsApp." },
      },
      confirm: { showSummary: true, showAddToCalendar: true, showReschedule: true, showCancel: true, showDirections: true, note: "" },
    },
    text: {},
  };
}
function baseQueueJourney(): Journey {
  const b = baseJourney();
  return {
    ...b, type: "queue", templateId: "qclassic",
    steps: {
      service: { display: "list", showDuration: false, showDescription: true, groupBy: "line-of-business", allowMultiple: false, maxServices: 1, lobs: ["personal", "business"], showImages: true, images: {} },
      location: { display: "list", radiusEnabled: true, radiusMi: 10, showHours: true, showServices: false, showDistance: true, showVideoOption: false, showPhoneOption: false, showWait: true, pauseEnabled: true, pauseOverMin: 45 },
      queue: { waitDisplay: "both", smsUpdates: true, singleEntry: true, holdMin: 10 },
      details: {
        fields: [
          { id: "first", label: "First name", type: "text", required: true, locked: true },
          { id: "last", label: "Last name", type: "text", required: true, locked: true },
          { id: "phone", label: "Mobile number", type: "tel", required: true, locked: true },
          { id: "email", label: "Email address", type: "email", required: false, locked: false },
        ],
        attendees: { enabled: false, max: 1, collectNames: false },
        consent: { required: true, policyUrl: "", marketingOptIn: false, marketingLabel: "Send me occasional news and updates.", whatsappOptIn: false, whatsappLabel: "I'd like to get updates about this visit on WhatsApp." },
      },
      ticket: { showNumber: true, showPosition: true, showEta: true, allowLeave: true, offerAppointment: true, note: "" },
    },
    text: { "details.headline": "Almost in — your details", "details.subtitle": "We text your place in line to your mobile." },
  };
}
function baseHubJourney(): Journey {
  const b = baseJourney();
  const q = baseQueueJourney();
  return {
    ...b, type: "hub", templateId: "hclassic", flowOrder: "hub",
    steps: {
      ...b.steps,
      service: { ...b.steps.service, allowMultiple: false },
      location: { ...b.steps.location, display: "list", radiusEnabled: false, showWait: true, pauseEnabled: true, pauseOverMin: 60, showVideoOption: false, showPhoneOption: false },
      queue: { ...q.steps.queue, chooseService: true },
      ticket: { ...q.steps.ticket },
      events: { enabled: true, showSpots: true, display: "list", rsvpNote: "" },
      hub: { layout: "stacked", showAppointments: true, showQueue: true },
    },
    text: {},
  };
}
function baseChatJourney(): Journey {
  const b = baseJourney();
  return {
    ...b, type: "chat", templateId: "cclassic", flowOrder: "conversational",
    steps: {
      chat: { assistantName: "Concierge", tone: "concierge", suggestions: true, greeting: "Hi! Tell me what you need and I'll sort out the right specialist, place and time — or just say “book me in” and we'll take it from there." },
      service: { display: "list", showDuration: true, showDescription: true, groupBy: "line-of-business", allowMultiple: false, maxServices: 1, lobs: ["personal", "homeloans", "wealth", "business"], showImages: true, images: {} },
      location: { display: "list", radiusEnabled: false, radiusMi: 10, showHours: true, showServices: false, showDistance: true, showVideoOption: false, showPhoneOption: false },
      staff: { enabled: true, choice: "optional", showRole: true, showRating: false, showNextAvail: true, showBio: false },
      details: {
        fields: [
          { id: "first", label: "First name", type: "text", required: true, locked: true },
          { id: "last", label: "Last name", type: "text", required: true, locked: true },
          { id: "phone", label: "Mobile number", type: "tel", required: true, locked: true },
          { id: "email", label: "Email address", type: "email", required: false, locked: false },
        ],
        attendees: { enabled: false, max: 1, collectNames: false },
        consent: { required: true, policyUrl: "", marketingOptIn: false, marketingLabel: "", whatsappOptIn: false, whatsappLabel: "I'd like to get updates about this event on WhatsApp." },
      },
      confirm: { showSummary: true, showAddToCalendar: true, showReschedule: true, showCancel: true, showDirections: false, note: "" },
    },
    text: {},
  };
}
function baseEventJourney(): Journey {
  const b = baseJourney();
  return {
    ...b, type: "event", templateId: "evlist", flowOrder: "events-first",
    steps: {
      events: { display: "list", showSpots: true, showImages: true, showLocation: true, showCategories: true, showHost: true, capacityBar: true, waitlist: true, images: {}, rsvpNote: "" },
      details: {
        fields: [
          { id: "first", label: "First name", type: "text", required: true, locked: true },
          { id: "last", label: "Last name", type: "text", required: true, locked: true },
          { id: "email", label: "Email address", type: "email", required: true, locked: true },
          { id: "phone", label: "Mobile number", type: "tel", required: false, locked: false },
        ],
        attendees: { enabled: true, max: 2, collectNames: false },
        consent: { required: true, policyUrl: "", marketingOptIn: false, marketingLabel: "", whatsappOptIn: true, whatsappLabel: "I'd like to get updates on WhatsApp." },
      },
    },
    text: { "details.headline": "Nearly there — your details", "details.subtitle": "Your reservation confirmation lands here." },
  };
}

/* ── Templates ─────────────────────────────────────────────────────── */
/** A starter template: `seed` is a deep-merge patch over the base journey. */
export interface JourneyTemplate { id: string; name: string; desc: string; tag: string; seed: Patch }
// ui-config — starter templates are product configuration (layout seeds), not records.
export const templates: JourneyTemplate[] = [
  { id: "classic", name: "Classic", desc: "Service first, clean list, thin progress bar. The proven default.", tag: "Most used", seed: {} },
  { id: "local", name: "Local first", desc: "Location leads — hours, distance and details up front, then services.", tag: "Multi-site", seed: { flowOrder: "location-first", theme: { progress: "steps" }, steps: { location: { display: "map-list" } } } },
  { id: "explorer", name: "Map explorer", desc: "Full-bleed map with a search radius as the opening screen.", tag: "Map view", seed: { flowOrder: "map-first", theme: { progress: "steps" }, steps: { location: { display: "map" }, service: { display: "tiles" } } } },
  { id: "concierge", name: "Concierge", desc: "Staff-led — customers pick who they meet. Tile services, tile progress.", tag: "Staff picker", seed: { theme: { progress: "tiles" }, steps: { staff: { choice: "required", showRating: true, showBio: true }, service: { display: "tiles" } } } },
];
export const queueTemplates: JourneyTemplate[] = [ // ui-config — starter templates (layout seeds), not records
  { id: "qclassic", name: "Queue classic", desc: "Service first, live waits on every branch card, phone-first join form.", tag: "Default", seed: {} },
  { id: "qlobby", name: "Lobby kiosk", desc: "Location leads — built for a tablet at the door. Hero welcome, big targets.", tag: "Location first", seed: { flowOrder: "location-first", header: { style: "hero" }, theme: { progress: "steps" } } },
];
export const hubTemplates: JourneyTemplate[] = [ // ui-config — starter templates (layout seeds), not records
  { id: "hclassic", name: "Location hub", desc: "Location first — then everything that place offers: book ahead, reserve an event seat, or join the live line.", tag: "All-in-one", seed: {} },
];
export const eventTemplates: JourneyTemplate[] = [ // ui-config — starter templates (layout seeds), not records
  { id: "evlist", name: "Events list", desc: "Chronological list with date chips, filters and places left. The clean default.", tag: "Default", seed: {} },
  { id: "evshowcase", name: "Showcase", desc: "Big imagery cards and a hero welcome — for open days, fairs and headline evenings.", tag: "Imagery", seed: { header: { style: "hero" }, theme: { progress: "steps" }, steps: { events: { display: "showcase" } }, text: { "welcome.headline": "What's on.", "welcome.body": "Upcoming events — reserve your place in a few taps." } } },
];
export const chatTemplates: JourneyTemplate[] = [ // ui-config — starter templates (layout seeds), not records
  { id: "cclassic", name: "Concierge chat", desc: "Assistant-led with quick-reply chips — tap or type, it keeps up either way.", tag: "Default", seed: {} },
  { id: "copen", name: "Open conversation", desc: "No chips — pure free-text. The assistant infers service, place, time and specialist.", tag: "AI-led", seed: { steps: { chat: { suggestions: false } } } },
];

export function makeJourney(templateId: string, name: string, type: string): Journey {
  const isQ = type === "queue", isC = type === "chat", isH = type === "hub", isE = type === "event";
  const list = isQ ? queueTemplates : isC ? chatTemplates : isH ? hubTemplates : isE ? eventTemplates : templates;
  const tpl = list.find((t) => t.id === templateId) || list[0];
  const j = deepMerge(isQ ? baseQueueJourney() : isC ? baseChatJourney() : isH ? baseHubJourney() : isE ? baseEventJourney() : baseJourney(), tpl.seed);
  j.id = "jrn_" + Date.now().toString(36);
  j.templateId = tpl.id;
  if (name) { j.name = name; j.slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 24) || "journey"; }
  return j;
}

/* ── Brand scan ────────────────────────────────────────────────────── */
/* The fabricated hash-derived scanner (BRAND_SCANS / SCAN_FALLBACK_PALETTES /
 * scanWebsite) is GONE (CC GM14 §3 item 1 — same fraud class fixed in
 * Settings → Branding). BrandScanCard (journey-panels) now calls the real
 * declared op `scanBrandFromWebsite` — ONE canonical scanner (REQ-076), same
 * guarded resolution as SettingsBranding; no invented results on any path. */
export function applyScanToJourney(scan: any): Patch {
  // Patch only the fields the server actually returned — never clobber theme
  // values with undefined from a partial scan result.
  const theme: Record<string, unknown> = { inheritBrand: false };
  for (const k of ["brandColor", "accentColor", "backgroundColor", "textColor", "headingFont", "bodyFont", "buttonShape"]) if (scan?.[k]) theme[k] = scan[k];
  const header: Record<string, unknown> = {};
  if (scan?.name) {
    header.brandName = scan.name;
    header.monogram = scan.monogram || String(scan.name).split(/\s+/).map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
  }
  if (scan?.tagline) header.tagline = scan.tagline;
  return { theme, ...(Object.keys(header).length ? { header } : {}) };
}

/* ── Render helpers ────────────────────────────────────────────────── */
import { Fonts } from "./journey-fonts";

/** Resolved, render-ready theme values (never partial — every field defaulted). */
export interface JourneyTheme {
  brandColor: string; accentColor: string; bg: string; text: string;
  headingFont: string; bodyFont: string; radius: number; cardRadius: number; progress: string;
}

export function isDarkColor(hex: string): boolean {
  const c = (hex || "#000").replace("#", "");
  const s = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
  const r = parseInt(s.slice(0, 2), 16) || 0, g = parseInt(s.slice(2, 4), 16) || 0, b = parseInt(s.slice(4, 6), 16) || 0;
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
}

export function resolveTheme(journey: Journey): JourneyTheme {
  const brand = getActiveBrand();
  const t = journey.theme;
  const src = t.inheritBrand ? {
    brandColor: brand.brandColor, accentColor: brand.accentColor, backgroundColor: brand.backgroundColor,
    textColor: brand.textColor, headingFont: brand.headingFont, bodyFont: brand.bodyFont, buttonShape: brand.buttonShape,
  } : t;
  Fonts.ensure(src.headingFont || "DM Sans"); Fonts.ensure(src.bodyFont || "DM Sans");
  return {
    brandColor: src.brandColor || BRAND_FALLBACK.brandColor, accentColor: src.accentColor || BRAND_FALLBACK.accentColor,
    bg: src.backgroundColor || BRAND_FALLBACK.backgroundColor, text: src.textColor || BRAND_FALLBACK.textColor,
    headingFont: src.headingFont || "DM Sans", bodyFont: src.bodyFont || "DM Sans",
    radius: src.buttonShape === "sharp" ? 0 : src.buttonShape === "pill" ? 999 : 8,
    cardRadius: src.buttonShape === "sharp" ? 0 : 12,
    progress: t.progress || "bar",
  };
}
export function journeyURL(j: Journey) { return "https://" + (j.domain || baseURL) + "/" + j.slug; }

export const slotDays = [
  { id: 0, label: "Today · Mon 20 Jul", short: "Mon 20", dow: "Mon", date: { y: 2026, m: 6, d: 20 } },
  { id: 1, label: "Tue 21 Jul", short: "Tue 21", dow: "Tue", date: { y: 2026, m: 6, d: 21 } },
  { id: 2, label: "Wed 22 Jul", short: "Wed 22", dow: "Wed", date: { y: 2026, m: 6, d: 22 } },
  { id: 3, label: "Thu 23 Jul", short: "Thu 23", dow: "Thu", date: { y: 2026, m: 6, d: 23 } },
  { id: 4, label: "Fri 24 Jul", short: "Fri 24", dow: "Fri", date: { y: 2026, m: 6, d: 24 }, full: true },
  { id: 5, label: "Mon 27 Jul", short: "Mon 27", dow: "Mon", date: { y: 2026, m: 6, d: 27 } },
  { id: 6, label: "Tue 28 Jul", short: "Tue 28", dow: "Tue", date: { y: 2026, m: 6, d: 28 } },
  { id: 7, label: "Wed 29 Jul", short: "Wed 29", dow: "Wed", date: { y: 2026, m: 6, d: 29 }, full: true },
  { id: 8, label: "Thu 30 Jul", short: "Thu 30", dow: "Thu", date: { y: 2026, m: 6, d: 30 } },
  { id: 9, label: "Fri 31 Jul", short: "Fri 31", dow: "Fri", date: { y: 2026, m: 6, d: 31 } },
  { id: 10, label: "Mon 3 Aug", short: "Mon 3", dow: "Mon", date: { y: 2026, m: 7, d: 3 } },
  { id: 11, label: "Tue 4 Aug", short: "Tue 4", dow: "Tue", date: { y: 2026, m: 7, d: 4 } },
  { id: 12, label: "Wed 5 Aug", short: "Wed 5", dow: "Wed", date: { y: 2026, m: 7, d: 5 } },
  { id: 13, label: "Thu 6 Aug", short: "Thu 6", dow: "Thu", date: { y: 2026, m: 7, d: 6 } },
  { id: 14, label: "Fri 7 Aug", short: "Fri 7", dow: "Fri", date: { y: 2026, m: 7, d: 7 } },
];
export const slotTimes = ["9:00", "9:30", "10:00", "10:30", "11:30", "1:00", "2:00", "2:30", "3:30", "4:00"];
export function slotMin(t: string) { const [h, m] = t.split(":").map(Number); return (h < 8 ? h + 12 : h) * 60 + m; }
export function fmtMin(min: number) { const h24 = Math.floor(min / 60) % 24, m = min % 60, ap = h24 >= 12 ? "PM" : "AM", h = h24 % 12 || 12; return h + ":" + String(m).padStart(2, "0") + " " + ap; }
export function staffFree(staffId: string, dayIdx: number, slotIdx: number) {
  let h = 0; for (let i = 0; i < staffId.length; i++) h = (h * 31 + staffId.charCodeAt(i)) % 97;
  return ((h + dayIdx * 5 + slotIdx * 3) % 10) > 2;
}

export function journeyServices(journey: Journey): any[] {
  return (journey.custom && journey.custom.services) || [];
}

export function journeyScreens(journey: Journey, mode?: string, isMobile?: boolean): string[] {
  if (journey.type === "chat") return ["chat"];
  if (journey.type === "event") return ["events", "event", "details", "eventdone"];
  if (journey.type === "hub") {
    const chooseSvc = (!journey.steps.queue || journey.steps.queue.chooseService !== false) && journeyServices(journey).length > 0;
    const qTail = chooseSvc ? ["service", "join", "ticket"] : ["join", "ticket"];
    const evTail = isMobile ? ["hubevents", "details", "eventdone"] : ["details", "eventdone"];
    const tail = mode === "queue" ? qTail : mode === "event" ? evTail : ["service", "staff", "time", "details", "confirm"];
    return ["location", "hub", ...tail];
  }
  const first = journey.flowOrder === "service-first" ? ["service", "location"] : ["location", "service"];
  if (journey.type === "queue") return [...first, "join", "ticket"];
  const rest: string[] = [];
  if (journey.steps.staff.enabled && journey.steps.staff.choice !== "off") rest.push("staff");
  rest.push("time", "details", "confirm");
  return [...first, ...rest];
}
export const SCREEN_LABELS: Record<string, string> = { service: "Service", location: "Location", staff: "Specialist", time: "Time", details: "Details", confirm: "Done", join: "Your details", ticket: "Ticket", chat: "Conversation", hub: "What's on", hubevents: "Events", events: "What's on", event: "Event", eventdone: "Done" };

export function tileImage(journey: Journey, th: any, svcId: string) {
  const imgs = journey.steps.service.images || {};
  const img = journey.steps.service.showImages !== false ? imgs[svcId] : null;
  if (!img) return null;
  if (img.kind === "upload" && img.src) return { backgroundImage: `url(${img.src})`, backgroundSize: "cover", backgroundPosition: "center" };
  const preset = TILE_PRESETS.find((p) => p.id === img.id);
  if (!preset) return null;
  return { background: preset.css || `linear-gradient(135deg, ${th.brandColor}, ${th.accentColor})` };
}
export function eventImage(journey: Journey, th: any, evId: string) {
  const st = journey.steps.events || {};
  const img = st.showImages !== false ? (st.images || {})[evId] : null;
  if (!img) return null;
  if (img.kind === "upload" && img.src) return { backgroundImage: `url(${img.src})`, backgroundSize: "cover", backgroundPosition: "center" };
  const preset = TILE_PRESETS.find((p) => p.id === img.id);
  if (!preset) return null;
  return { background: preset.css || `linear-gradient(135deg, ${th.brandColor}, ${th.accentColor})` };
}
